import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

/**
 * Styled multi-image picker with thumbnail previews — replaces the raw browser
 * file input ("Choose Files / No file chosen") used for new-colour variant
 * photos. Appends to the current selection (so you can add more), lets each
 * thumbnail be removed, and manages object-URL lifecycle (revoked on change /
 * unmount). Controlled: parent owns the File[].
 */
const ImagePicker: FC<{
  files: File[]
  onChange: (files: File[]) => void
  disabled?: boolean
}> = ({files, onChange, disabled}) => {
  const {formatMessage} = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [previews, setPreviews] = useState<string[]>([])

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f))
    setPreviews(urls)
    return () => urls.forEach((u) => URL.revokeObjectURL(u))
  }, [files])

  const addFiles = (incoming: FileList | null) => {
    if (incoming && incoming.length > 0) {
      onChange([...files, ...Array.from(incoming)])
    }
    // Reset so re-selecting the same file still fires onChange.
    if (inputRef.current) inputRef.current.value = ''
  }
  const removeAt = (i: number) => onChange(files.filter((_, idx) => idx !== i))

  return (
    <div className='avp-imgpicker'>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        multiple
        hidden
        disabled={disabled}
        onChange={(e) => addFiles(e.target.files)}
      />
      <button
        type='button'
        className='avp-imgpicker-btn'
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <i className='fas fa-image' aria-hidden='true' />
        {files.length > 0
          ? formatMessage({id: 'offers.variants.add_more_images', defaultMessage: 'Add more'})
          : formatMessage({id: 'offers.variants.choose_images', defaultMessage: 'Choose images'})}
      </button>

      {files.length > 0 && (
        <div className='avp-imgpicker-grid'>
          {previews.map((src, i) => (
            <div className='avp-thumb' key={i}>
              <img src={src} alt='' />
              {!disabled && (
                <button
                  type='button'
                  className='avp-thumb-x'
                  onClick={() => removeAt(i)}
                  aria-label={formatMessage({id: 'remove', defaultMessage: 'Remove'})}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImagePicker
